import { useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Play,
  FileText,
  HelpCircle,
  Lock,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Download,
  BookOpen,
  Clock,
} from 'lucide-react';
import {
  useCoursePlayer,
  useLesson,
  useLessonCompleteMutation,
} from '@/hooks/useCourses';
import { cn } from '@/lib/utils';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

const lessonTypeIcons: Record<string, typeof Play> = {
  video: Play,
  text: FileText,
  quiz: HelpCircle,
  exercise: HelpCircle,
};

function getLessonIcon(type: string) {
  return lessonTypeIcons[type] || FileText;
}

/** Build a flat ordered list of all lessons for prev/next navigation */
function flattenLessons(modules: any[]): { moduleId: string; lessonId: string; title: string }[] {
  const list: { moduleId: string; lessonId: string; title: string }[] = [];
  for (const mod of modules) {
    if (!mod.is_locked) {
      for (const lesson of mod.lessons ?? []) {
        list.push({ moduleId: mod.id, lessonId: lesson.id, title: lesson.title });
      }
    }
  }
  return list;
}

export default function CoursePlayer() {
  const { t } = useTranslation('account');
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId?: string }>();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const {
    data: courseData,
    isLoading: courseLoading,
    isError: courseError,
  } = useCoursePlayer(courseId!);

  const {
    data: lessonData,
    isLoading: lessonLoading,
  } = useLesson(courseId!, lessonId!);

  const completeMutation = useLessonCompleteMutation();

  const course = courseData?.course;
  const enrollment = courseData?.enrollment;
  const modules: any[] = course?.modules ?? [];
  const lesson = lessonData?.lesson;

  const allLessons = flattenLessons(modules);
  const currentIndex = lessonId
    ? allLessons.findIndex((l) => l.lessonId === lessonId)
    : -1;
  const prevLesson = currentIndex > 0 ? allLessons[currentIndex - 1] : null;
  const nextLesson =
    currentIndex >= 0 &&
    currentIndex < allLessons.length - 1
      ? allLessons[currentIndex + 1]
      : null;

  const firstLesson = allLessons.length > 0 ? allLessons[0] : null;

  const handleMarkComplete = useCallback(() => {
    if (!courseId || !lessonId) return;
    completeMutation.mutate(
      { courseId, lessonId },
      {
        onSuccess: () => {
          if (nextLesson) {
            navigate(`/account/courses/${courseId}/lesson/${nextLesson.lessonId}`);
          }
        },
      }
    );
  }, [courseId, lessonId, completeMutation, nextLesson, navigate]);

  const navigateToLesson = useCallback(
    (id: string) => {
      navigate(`/account/courses/${courseId}/lesson/${id}`);
      setSidebarOpen(false);
    },
    [courseId, navigate]
  );

  if (courseLoading) {
    return <LoadingSpinner className="py-16" />;
  }

  if (courseError || !course) {
    return (
      <div className="text-center py-16">
        <p className="text-ink-muted mb-4">{t('coursePlayer.errorLoading')}</p>
        <Link to="/account/courses">
          <Button variant="outline">{t('coursePlayer.backToCourses')}</Button>
        </Link>
      </div>
    );
  }

  const completionPct = enrollment?.completion_percentage ?? 0;

  return (
    <div className="flex flex-col -mx-1 sm:-mx-0">
      {/* Top progress bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-display font-bold text-ink truncate pr-4">
            {course.title}
          </h2>
          <span className="text-sm font-medium text-ink-muted shrink-0">
            {t('coursePlayer.completionPct', { pct: Math.round(completionPct) })}
          </span>
        </div>
        <div className="h-2 w-full rounded-full bg-line overflow-hidden">
          <div
            className="h-full rounded-full bg-sage-500 transition-all duration-300"
            style={{ width: `${Math.min(100, completionPct)}%` }}
          />
        </div>
      </div>

      {/* Mobile sidebar toggle */}
      <div className="lg:hidden mb-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? (
            <X className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Menu className="h-4 w-4" aria-hidden="true" />
          )}
          {sidebarOpen ? t('coursePlayer.hideLessons') : t('coursePlayer.showLessons')}
        </Button>
      </div>

      <div className="flex gap-6">
        {/* Left sidebar - module/lesson tree */}
        <aside
          className={cn(
            'w-72 shrink-0 bg-surface border border-line rounded-xl overflow-hidden',
            'lg:block',
            sidebarOpen ? 'block absolute z-20 lg:relative shadow-lg lg:shadow-sm' : 'hidden'
          )}
        >
          <div className="p-3 border-b border-line bg-surface-soft">
            <p className="text-xs font-semibold text-ink-muted uppercase tracking-wider">
              {t('coursePlayer.courseContent')}
            </p>
          </div>
          <nav className="overflow-y-auto max-h-[calc(100vh-16rem)]">
            {modules.map((mod: any) => (
              <div key={mod.id}>
                {/* Module header */}
                <div className="flex items-center gap-2 px-3 py-2.5 bg-surface-soft border-b border-line">
                  {mod.is_locked ? (
                    <Lock className="h-4 w-4 text-ink-muted shrink-0" aria-hidden="true" />
                  ) : (
                    <BookOpen className="h-4 w-4 text-sage-600 shrink-0" aria-hidden="true" />
                  )}
                  <span
                    className={cn(
                      'text-sm font-semibold truncate',
                      mod.is_locked ? 'text-ink-muted' : 'text-ink'
                    )}
                  >
                    {mod.title}
                  </span>
                </div>

                {/* Lessons in module */}
                {!mod.is_locked &&
                  (mod.lessons ?? []).map((l: any) => {
                    const Icon = getLessonIcon(l.type);
                    const isActive = l.id === lessonId;
                    return (
                      <button
                        key={l.id}
                        onClick={() => navigateToLesson(l.id)}
                        className={cn(
                          'w-full flex items-center gap-2 px-4 py-2 text-left text-sm transition-colors',
                          'hover:bg-sage-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sage-500',
                          isActive
                            ? 'bg-sage-50 text-sage-800 font-medium border-l-2 border-sage-600'
                            : 'text-ink-soft'
                        )}
                      >
                        {l.is_completed ? (
                          <CheckCircle
                            className="h-4 w-4 text-success-500 shrink-0"
                            aria-label={t('coursePlayer.completedLabel')}
                          />
                        ) : (
                          <Icon className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden="true" />
                        )}
                        <span className="truncate flex-1">{l.title}</span>
                        {l.duration_minutes && (
                          <span className="text-xs text-ink-muted shrink-0 flex items-center gap-0.5">
                            <Clock className="h-3 w-3" aria-hidden="true" />
                            {t('coursePlayer.durationMinutesShort', { minutes: l.duration_minutes })}
                          </span>
                        )}
                      </button>
                    );
                  })}
              </div>
            ))}
          </nav>
        </aside>

        {/* Main content area */}
        <main className="flex-1 min-w-0">
          {lessonId && lesson ? (
            <div className="space-y-6">
              {/* Lesson title */}
              <div>
                <h3 className="text-xl font-display font-bold text-ink">
                  {lesson.title}
                </h3>
                {lesson.duration_minutes && (
                  <p className="text-sm text-ink-muted mt-1 flex items-center gap-1">
                    <Clock className="h-4 w-4" aria-hidden="true" />
                    {t('coursePlayer.durationMinutes', { minutes: lesson.duration_minutes })}
                  </p>
                )}
              </div>

              {/* Lesson content by type */}
              {lessonLoading ? (
                <LoadingSpinner className="py-12" />
              ) : (
                <div className="bg-surface border border-line rounded-xl overflow-hidden">
                  {lesson.type === 'video' && lesson.signed_video_url && (
                    <div className="aspect-video bg-black">{/* dark-exempt: video letterboxing is black in both modes */}
                      <video
                        key={lesson.signed_video_url}
                        src={lesson.signed_video_url}
                        controls
                        className="w-full h-full"
                        controlsList="nodownload"
                      >
                        {t('coursePlayer.videoFallback')}
                      </video>
                    </div>
                  )}

                  {(lesson.type === 'text' || lesson.content_html) && (
                    <div
                      className="prose prose-stone max-w-none p-6"
                      dangerouslySetInnerHTML={{ __html: lesson.content_html ?? '' }}
                    />
                  )}

                  {(lesson.type === 'quiz' || lesson.type === 'exercise') &&
                    lesson.content_html && (
                      <div
                        className="prose prose-stone max-w-none p-6"
                        dangerouslySetInnerHTML={{ __html: lesson.content_html }}
                      />
                    )}
                </div>
              )}

              {/* Downloadable resources */}
              {lesson.resources && lesson.resources.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-ink-soft mb-2">
                    {t('coursePlayer.resources')}
                  </h4>
                  <div className="space-y-2">
                    {lesson.resources.map((resource: any) => (
                      <a
                        key={resource.id ?? resource.url}
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        className="inline-flex items-center gap-2 text-sm text-sage-700 hover:text-sage-800 hover:underline"
                      >
                        <Download className="h-4 w-4" aria-hidden="true" />
                        {resource.title ?? resource.filename ?? t('coursePlayer.downloadFallback')}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Mark complete + nav buttons */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-4 border-t border-line">
                <div className="flex gap-2">
                  {prevLesson && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigateToLesson(prevLesson.lessonId)}
                    >
                      <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                      {t('coursePlayer.previous')}
                    </Button>
                  )}
                  {nextLesson && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigateToLesson(nextLesson.lessonId)}
                    >
                      {t('coursePlayer.next')}
                      <ChevronRight className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  )}
                </div>

                {!lesson.is_completed && (
                  <Button
                    onClick={handleMarkComplete}
                    isLoading={completeMutation.isPending}
                  >
                    <CheckCircle className="h-4 w-4" aria-hidden="true" />
                    {t('coursePlayer.markComplete')}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            /* No lesson selected - course overview */
            <div className="bg-surface border border-line rounded-xl p-8 text-center">
              <BookOpen
                className="h-16 w-16 text-ink-muted mx-auto mb-4"
                aria-hidden="true"
              />
              <h3 className="text-xl font-display font-bold text-ink mb-2">
                {course.title}
              </h3>
              {course.description && (
                <p className="text-ink-muted mb-6 max-w-lg mx-auto">
                  {course.description}
                </p>
              )}
              <div className="flex items-center justify-center gap-4 text-sm text-ink-muted mb-6">
                <span>{t('coursePlayer.moduleCount', { count: modules.length })}</span>
                <span className="text-ink-muted">|</span>
                <span>{t('coursePlayer.lessonCount', { count: allLessons.length })}</span>
              </div>
              {firstLesson ? (
                <Button onClick={() => navigateToLesson(firstLesson.lessonId)}>
                  {t('coursePlayer.startFirstLesson')}
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </Button>
              ) : (
                <p className="text-ink-muted text-sm">
                  {t('coursePlayer.noLessons')}
                </p>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
